import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, MoreHorizontal, BellRing, User, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  FOLLOWUP_TYPES, FOLLOWUP_STATUSES, FOLLOWUP_OUTCOMES,
  followupStatusMeta, followupPriorityMeta, fmtDateTime,
} from "@/config/crm";
import { FollowUpDialog } from "@/components/followups/FollowUpDialog";

export default function FollowUps() {
  const navigate = useNavigate();
  const [followups, setFollowups] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [owner, setOwner] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [completeFor, setCompleteFor] = useState(null);
  const [outcome, setOutcome] = useState(FOLLOWUP_OUTCOMES[0]);

  useEffect(() => { api.get("/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (type !== "all") params.type = type;
    if (owner !== "all") params.owner = owner;
    try { const { data } = await api.get("/followups", { params }); setFollowups(data.followups); }
    finally { setLoading(false); }
  }, [status, type, owner]);

  useEffect(() => { load(); }, [load]);

  const ownerName = (oid) => owners.find((o) => o.id === oid)?.name || "Unassigned";

  const assignOwner = async (id, oid) => {
    try { await api.put(`/followups/${id}`, { owner_id: oid }); toast.success("Owner assigned"); load(); }
    catch { toast.error("Failed to assign"); }
  };
  const cancelFu = async (id) => {
    try { await api.put(`/followups/${id}`, { status: "cancelled" }); toast.success("Follow-up cancelled"); load(); }
    catch { toast.error("Failed"); }
  };
  const doComplete = async () => {
    try {
      await api.put(`/followups/${completeFor.id}`, { status: "completed", outcome });
      toast.success("Follow-up completed");
      setCompleteFor(null); load();
    } catch { toast.error("Failed to complete"); }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (f) => { setEditing(f); setDialogOpen(true); };

  const pendingCount = followups.filter((f) => f.status === "pending").length;
  const overdueCount = followups.filter((f) => f.is_overdue).length;

  return (
    <div className="animate-fade-up space-y-6" data-testid="followups-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Follow-ups</h1>
          <p className="mt-1 text-sm text-slate-500">
            <span data-testid="followups-total" className="font-medium text-slate-700">{followups.length}</span> total ·{" "}
            <span className="text-sky-700">{pendingCount} pending</span> ·{" "}
            <span className="text-red-600">{overdueCount} overdue</span>
          </p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-followup-button">
          <Plus className="h-4 w-4" /> New Follow-up
        </Button>
      </div>

      <Card className="p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:flex">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="lg:w-44" data-testid="fu-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{FOLLOWUP_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="lg:w-52" data-testid="fu-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger className="lg:w-48" data-testid="fu-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Owners</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          {(status !== "all" || type !== "all" || owner !== "all") && (
            <Button variant="ghost" onClick={() => { setStatus("all"); setType("all"); setOwner("all"); }}>Clear</Button>
          )}
        </div>
      </Card>

      {/* Desktop table */}
      <Card className="hidden overflow-hidden shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Regarding</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Due</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Owner</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
              <TableHead className="pr-6 text-right text-xs uppercase tracking-wider text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={7} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
              : followups.length === 0 ? <TableRow><TableCell colSpan={7} className="py-16 text-center text-sm text-slate-400" data-testid="followups-empty">No follow-ups match your filters.</TableCell></TableRow>
              : followups.map((f) => {
                const pr = followupPriorityMeta(f.priority);
                return (
                  <TableRow key={f.id} className="border-slate-100" data-testid="followup-row">
                    <TableCell className="pl-6">
                      <p className="font-medium text-slate-900">{f.patient_name || f.lead_name || "—"}</p>
                      {f.notes && <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">{f.notes}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{f.type}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{fmtDateTime(f.due_at)}</TableCell>
                    <TableCell><Badge className={cn(pr.badge)}>{pr.label}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{ownerName(f.owner_id)}</TableCell>
                    <TableCell>
                      {f.is_overdue
                        ? <Badge className="bg-red-50 text-red-600 hover:bg-red-50" data-testid="overdue-badge">Overdue</Badge>
                        : <Badge className={cn(followupStatusMeta(f.status).badge)}>{followupStatusMeta(f.status).label}</Badge>}
                    </TableCell>
                    <TableCell className="pr-6 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`followup-actions-${f.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {f.status === "pending" && <DropdownMenuItem onClick={() => { setCompleteFor(f); setOutcome(FOLLOWUP_OUTCOMES[0]); }} data-testid={`complete-${f.id}`}>Complete…</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => openEdit(f)}>Edit</DropdownMenuItem>
                          {f.lead_id && <DropdownMenuItem onClick={() => navigate(`/leads/${f.lead_id}`)}><ExternalLink className="h-4 w-4" /> Open Lead</DropdownMenuItem>}
                          {f.patient_id && <DropdownMenuItem onClick={() => navigate(`/patients/${f.patient_id}`)}><ExternalLink className="h-4 w-4" /> Open Patient</DropdownMenuItem>}
                          {owners.map((o) => <DropdownMenuItem key={o.id} onClick={() => assignOwner(f.id, o.id)}><User className="h-4 w-4" /> Assign {o.name}</DropdownMenuItem>)}
                          {f.status !== "cancelled" && <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => cancelFu(f.id)}>Cancel</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {loading ? [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)
          : followups.map((f) => {
            const pr = followupPriorityMeta(f.priority);
            return (
              <Card key={f.id} className="p-4 shadow-sm" data-testid="followup-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><BellRing className="h-4 w-4" /></div>
                    <div>
                      <p className="font-medium text-slate-900">{f.patient_name || f.lead_name || "—"}</p>
                      <p className="text-xs text-slate-500">{f.type} · due {fmtDateTime(f.due_at)}</p>
                    </div>
                  </div>
                  {f.is_overdue ? <Badge className="bg-red-50 text-red-600">Overdue</Badge> : <Badge className={cn(followupStatusMeta(f.status).badge)}>{followupStatusMeta(f.status).label}</Badge>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <Badge className={cn(pr.badge)}>{pr.label}</Badge>
                  {f.status === "pending" && <Button size="sm" variant="outline" onClick={() => { setCompleteFor(f); setOutcome(FOLLOWUP_OUTCOMES[0]); }}>Complete</Button>}
                </div>
              </Card>
            );
          })}
      </div>

      <FollowUpDialog open={dialogOpen} onOpenChange={setDialogOpen} followup={editing} owners={owners} onSaved={load} />

      {/* Complete dialog */}
      <Dialog open={Boolean(completeFor)} onOpenChange={(v) => !v && setCompleteFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="complete-followup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Complete Follow-up</DialogTitle>
            <DialogDescription>Record the outcome of this follow-up.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-1.5">
            <Label>Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="outcome-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FOLLOWUP_OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteFor(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doComplete} data-testid="confirm-complete">Mark Completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
